/**
 * Image plans in the map editor: the uploaded image is laid on a fixed
 * patch of the map at 0°/0°, where Web Mercator is close to linear, so
 * positions, lines, text and view areas use the same code as geo plans.
 *
 * The image is IMAGE_FRAME_WIDTH_M wide (about 1 km) and keeps its aspect
 * ratio; view area reaches and lengths are in those frame metres.
 */
/** Width of the image on the map, in metres. */
export const IMAGE_FRAME_WIDTH_M = 1000;
const M_PER_DEG = 111_319.49;
const SPAN_DEG = IMAGE_FRAME_WIDTH_M / M_PER_DEG;
/** Height / width of an image, clamped to something drawable. */
export function clampImageAspect(aspect) {
    return Number.isFinite(aspect) && aspect > 0
        ? Math.max(0.05, Math.min(20, aspect))
        : 1;
}
/** A point of the image ([0..1] from the left, [0..1] from the top). */
export function imageFractionToLngLat(x, y, aspect) {
    const a = clampImageAspect(aspect);
    return [(x - 0.5) * SPAN_DEG, (0.5 - y) * SPAN_DEG * a];
}
/** Corners of the image (top-left, top-right, bottom-right, bottom-left). */
export function imageFrameCorners(aspect) {
    return [
        imageFractionToLngLat(0, 0, aspect),
        imageFractionToLngLat(1, 0, aspect),
        imageFractionToLngLat(1, 1, aspect),
        imageFractionToLngLat(0, 1, aspect),
    ];
}
/** [west, south, east, north] of the image. */
export function imageFrameBBox(aspect) {
    const [tl, , br] = imageFrameCorners(aspect);
    return [tl[0], br[1], br[0], tl[1]];
}
/** A generic element for an old overlay; image overlays have none. */
export function legacyOverlayToItem(overlay, aspect) {
    const color = /^#[0-9a-fA-F]{6}$/.test(overlay.color ?? "")
        ? overlay.color.toUpperCase()
        : undefined;
    const at = (x, y) => imageFractionToLngLat(x, y, aspect);
    const { positionX: cx, positionY: cy, width: w, height: h } = overlay;
    const width = Math.max(1, Math.min(12, overlay.strokeWidth ?? 2));
    switch (overlay.overlayType) {
        case "text":
            return {
                symbolType: "text",
                label: overlay.label?.trim() || "Text",
                point: at(cx, cy),
                line: null,
                style: {
                    ...(color ? { color } : {}),
                    size: Math.max(10, Math.min(48, Math.round(overlay.fontSize ?? 14))),
                },
            };
        case "line": {
            const points = overlay.points?.length
                ? overlay.points
                : [
                    { x: cx - 0.1, y: cy },
                    { x: cx + 0.1, y: cy },
                ];
            if (points.length < 2)
                return null;
            return {
                symbolType: "line",
                label: overlay.label?.trim() || null,
                point: null,
                line: points.map((p) => at(p.x, p.y)),
                style: { ...(color ? { color } : {}), width },
            };
        }
        case "rectangle":
        case "ellipse": {
            const ring = [];
            if (overlay.overlayType === "rectangle") {
                for (const [dx, dy] of [
                    [-1, -1],
                    [1, -1],
                    [1, 1],
                    [-1, 1],
                    [-1, -1],
                ]) {
                    ring.push(at(cx + (dx * w) / 2, cy + (dy * h) / 2));
                }
            }
            else {
                for (let i = 0; i <= 32; i++) {
                    const t = (i / 32) * Math.PI * 2;
                    ring.push(at(cx + (Math.cos(t) * w) / 2, cy + (Math.sin(t) * h) / 2));
                }
            }
            return {
                symbolType: "line",
                label: overlay.label?.trim() || null,
                point: null,
                line: ring,
                style: { ...(color ? { color } : {}), width },
            };
        }
        default:
            return null;
    }
}
